import React from 'react';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  variant?: 'rectangular' | 'circular' | 'rounded';
}

/**
 * Base animated skeleton element with smooth shimmer wave
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'rounded',
  ...props
}) => {
  const variantClass = 
    variant === 'circular' ? 'rounded-full' :
    variant === 'rounded' ? 'rounded-sm' : 'rounded-none';

  return (
    <div
      className={`skeleton-shimmer ${variantClass} ${className}`}
      {...props}
    />
  );
};

/**
 * Skeleton component mirroring the exact layout and dimensions of ProductCard
 */
export const SkeletonProductCard: React.FC = () => {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm flex flex-col justify-between overflow-hidden shadow-xs pointer-events-none select-none">
      {/* Product Image Area Skeleton */}
      <div className="relative w-full aspect-square bg-neutral-50 flex items-center justify-center p-4 sm:p-6 overflow-hidden border-b border-neutral-100">
        {/* Badge Placeholder */}
        <div className="absolute top-3 left-3 z-10">
          <Skeleton className="w-14 h-5 rounded-none" />
        </div>

        {/* Custom Tag Placeholder */}
        <div className="absolute top-3 right-3 z-10">
          <Skeleton className="w-16 h-5 rounded-xs" />
        </div>

        {/* Centered Image Placeholder */}
        <div className="w-3/4 h-3/4 flex items-center justify-center">
          <Skeleton className="w-full h-full rounded-md opacity-70" />
        </div>
      </div>

      {/* Product Details Skeleton */}
      <div className="p-4 flex flex-col flex-1 justify-between space-y-3 bg-white">
        <div className="space-y-2">
          {/* Category Placeholder */}
          <div className="flex items-center justify-between">
            <Skeleton className="w-20 h-3" />
            <Skeleton className="w-10 h-3" />
          </div>

          {/* Title Placeholder */}
          <Skeleton className="w-4/5 h-5 mt-1" />

          {/* Variants Line Placeholder */}
          <Skeleton className="w-3/5 h-3 mt-1 opacity-75" />
        </div>

        {/* Price & Button Skeleton */}
        <div className="pt-2 border-t border-neutral-100 flex items-center justify-between gap-2">
          <div className="space-y-1">
            <Skeleton className="w-16 h-5" />
            <Skeleton className="w-20 h-2.5 opacity-60" />
          </div>

          {/* Action button placeholder */}
          <Skeleton className="w-24 h-8 rounded-sm" />
        </div>
      </div>
    </div>
  );
};

/**
 * Responsive grid of SkeletonProductCards
 */
interface SkeletonGridProps {
  count?: number;
}

export const SkeletonGrid: React.FC<SkeletonGridProps> = ({ count = 8 }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
      {Array.from({ length: count }).map((_, idx) => (
        <SkeletonProductCard key={`skeleton-card-${idx}`} />
      ))}
    </div>
  );
};

/**
 * Skeleton for Product Detail Modal while loading product content or images
 */
export const SkeletonDetailModal: React.FC = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 sm:gap-8 p-6 sm:p-8 animate-pulse">
      {/* Left Column: Image Skeleton */}
      <div className="md:col-span-5 space-y-4">
        <Skeleton className="w-full aspect-square rounded-md" />
        <div className="space-y-2">
          <Skeleton className="w-full h-8 rounded-sm" />
          <Skeleton className="w-full h-8 rounded-sm" />
        </div>
      </div>

      {/* Right Column: Form Skeleton */}
      <div className="md:col-span-7 space-y-5">
        <div className="space-y-2">
          <Skeleton className="w-24 h-4" />
          <Skeleton className="w-3/4 h-8" />
          <Skeleton className="w-32 h-6" />
        </div>

        <div className="space-y-3 pt-4 border-t border-neutral-200">
          <Skeleton className="w-28 h-4" />
          <div className="flex gap-2">
            <Skeleton className="w-20 h-9" />
            <Skeleton className="w-20 h-9" />
            <Skeleton className="w-20 h-9" />
          </div>
        </div>

        <div className="space-y-2 pt-4">
          <Skeleton className="w-32 h-4" />
          <Skeleton className="w-full h-12" />
        </div>

        <div className="pt-4">
          <Skeleton className="w-full h-12 rounded-sm" />
        </div>
      </div>
    </div>
  );
};
